import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export default function BannerManager() {
  const [banners, setBanners] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [form, setForm] = useState({
    desktopSrc: "",
    alt: "",
    title: "",
    subheading: "",
    link: "",
    type: "Type 1",
  });

  const fetchBanners = async () => {
    const res = await fetch("https://api.solarstation.in/banner/getBanner");
    const data = await res.json();
    setBanners(data.banners);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageFile) {
      toast.error("Please select an image to upload.");
      return;
    }

    toast.promise(uploadAndSubmitBanner(), {
      loading: "Uploading image & saving banner...",
      success: "Banner added successfully!",
      error: "Failed to add banner.",
    });
  };

  const uploadAndSubmitBanner = async () => {
    // Upload Image First
    const formData = new FormData();
    formData.append("image", imageFile);

    const imageRes = await fetch("https://api.solarstation.in/image/upload", {
      method: "POST",
      body: formData,
    });

    const imageData = await imageRes.json();

    if (!imageData?.imageUrl) {
      throw new Error("Image upload failed");
    }

    const updatedForm = { ...form, desktopSrc: imageData.imageUrl };

    // Save banner
    await fetch("https://api.solarstation.in/banner/createBanner", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updatedForm),
    });

    // Reset state
    setForm({
      desktopSrc: "",
      alt: "",
      title: "",
      subheading: "",
      link: "",
      type: "Type 1",
    });
    setImageFile(null);
    fetchBanners();
  };

  const deleteBanner = async (id) => {
    await fetch(`https://api.solarstation.in/banner/deleteBanner/${id}`, {
      method: "DELETE",
    });
    toast.success("Banner deleted");
    fetchBanners();
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">
        Manage Homepage Banners
      </h1>

      <form
        onSubmit={handleSubmit}
        className="bg-white p-6 rounded-xl shadow space-y-4 mb-10"
      >
        {/* Image Selection */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Upload Image
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files[0])}
            className="w-full p-2 border border-gray-300 rounded-lg"
          />
        </div>

        {/* Alt & Title */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Alt Text
            </label>
            <input
              type="text"
              placeholder="Enter alt text"
              value={form.alt}
              onChange={(e) => setForm({ ...form, alt: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              placeholder="Enter title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        {/* Subheading & Link */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Subheading
            </label>
            <input
              type="text"
              placeholder="Enter subheading"
              value={form.subheading}
              onChange={(e) =>
                setForm({ ...form, subheading: e.target.value })
              }
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Link
            </label>
            <input
              type="text"
              placeholder="Enter link"
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>

        {/* Banner Type Dropdown */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Banner Type
          </label>
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full p-2 border border-gray-300 rounded-lg"
            required
          >
            <option value="Type 1">Type 1</option>
            <option value="Type 2">Type 2</option>
            <option value="Type 3">Type 3</option>
            <option value="Type 4">Type 4</option>
          </select>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-lg transition w-full mt-4"
        >
          ➕ Add Banner
        </button>
      </form>

      {/* Display Existing Banners */}
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">
        Current Banners
      </h2>

      <div className="space-y-4">
        {banners.map((b) => (
          <div
            key={b._id}
            className="flex items-center justify-between bg-white shadow p-4 rounded-lg"
          >
            <div className="flex items-center gap-4">
              <img
                src={b.desktopSrc}
                alt={b.alt}
                className="w-24 h-16 rounded object-cover"
              />
              <div>
                <p className="font-medium text-gray-700">Title: {b.title}</p>
                <p className="text-sm text-gray-500">Alt: {b.alt}</p>
                <p className="text-sm text-gray-500">
                  Subheading: {b.subheading}
                </p>
                <p className="text-sm text-blue-600">Link: {b.link}</p>
                <p className="text-sm text-purple-600 font-semibold">
                  Type: {b.type}
                </p>
              </div>
            </div>
            <button
              onClick={() => deleteBanner(b._id)}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-1 rounded transition"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
